import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, Typography,
  TablePagination, InputAdornment, TextField, Box, Paper,
  IconButton, Divider, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import * as XLSX from "xlsx";
import DownloadIcon from '@mui/icons-material/Download';
import api from "../api";
import AgregarDocumentacion from "./utilities/AgregarDocumentacion";
import EditarDocumentacion from "./utilities/EditarDocumentacion";
import DetalleDocumentacion from "./utilities/DetalleDocumentacion";

const Documentos = () => {
  const [documentos, setDocumentos] = useState([]);
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentacion, setSelectedDocumentacion] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuDoc, setMenuDoc] = useState(null);

  useEffect(() => {
    fetchDocumentos();
  }, []);

  const fetchDocumentos = () => {
    api.get("documentos/")
      .then((response) => {
        setDocumentos(response.data);
      })
      .catch((error) => {
        console.error("Error al obtener documentos:", error);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm("¿Eliminar esta documentación?")) {
      api.delete(`documentos/${id}/`)
        .then(() => {
          setDocumentos(prev => prev.filter(d => d.id !== id));
        })
        .catch((error) => {
          console.error("Error al eliminar documentación:", error);
        });
    }
  };

  const handleMenuOpen = (event, doc) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuDoc(doc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuDoc(null);
  };

  const handleEditOpen = () => {
    setOpenEdit(true);
    setAnchorEl(null);
  };

  const handleRowClick = (doc) => {
    setSelectedDocumentacion(doc);
  };

  const handleDetailClose = () => {
    setSelectedDocumentacion(null);
  };

  const handleDocumentoUpdated = (updatedDocumento) => {
    setDocumentos((prevDocumento) => {
      const exists = prevDocumento.some((i) => i.id === updatedDocumento.id);
      if (exists) {
        return prevDocumento.map((i) =>
          i.id === updatedDocumento.id ? updatedDocumento : i
        );
      } else {
        return [...prevDocumento, updatedDocumento];
      }
    });

    setOpen(false);      // para AgregarInfraccion
    setOpenEdit(false);  // para EditarInfraccion
  };

  const normalize = (str) =>
    str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const filteredDocumentos = documentos.filter((doc) => {
    const search = normalize(searchTerm);

    return (
      normalize(doc.vehiculo_nombre || "").includes(search) ||
      normalize(doc.tipo || "").includes(search) ||
      normalize(doc.numero || "").includes(search) ||
      normalize(
        new Date(doc.fecha_vencimiento).toLocaleString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      ).includes(search)
    );
  });

  // Download all records as XLS
  const handleDownloadXLS = async () => {
    try {
      const response = await api.get("documentos/");
      const allData = response.data;

      const worksheet = XLSX.utils.json_to_sheet(allData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Documentos");

      XLSX.writeFile(workbook, "documentos.xlsx");
    } catch (error) {
      alert("Error al descargar los datos.");
      console.error(error);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700} color='#152d51'>
          Documentos
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            variant="outlined"
            label="Buscar documentación"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button onClick={handleDownloadXLS} variant="outlined" color="success">
            Descargar XLS&nbsp;<DownloadIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
          <Button onClick={() => setOpen(true)} style={{ border: "1px solid #3f51b5", borderRadius: "6px", color: "#3f51b5" }}>
            Agregar Documento <AddIcon sx={{ fontSize: 18, marginTop: "-2px" }} />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "rgb(244, 246, 248)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Vehículo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nro</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fecha de Vencimiento</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDocumentos
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((doc) => (
                <TableRow key={doc.id} sx={{ '&:hover': { backgroundColor: '#f1f1f1', cursor: 'pointer' } }} onClick={() => handleRowClick(doc)}>
                  <TableCell>{doc.vehiculo_nombre}</TableCell>
                  <TableCell>{doc.tipo}</TableCell>
                  <TableCell>{doc.numero}</TableCell>
                  <TableCell>
                    {new Date(doc.fecha_vencimiento).toLocaleString("es-ES", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, doc)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={filteredDocumentos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditOpen}>
          <EditIcon color="primary" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <MenuItem onClick={() => { handleDelete(menuDoc.id); handleMenuClose(); }}>
          <DeleteIcon color="error" sx={{ mr: 1 }} /> Eliminar
        </MenuItem>
      </Menu>

      {/* Detalle */}
      <Dialog open={!!selectedDocumentacion} onClose={handleDetailClose} fullWidth maxWidth="sm">
        <DialogContent>
          <DetalleDocumentacion selectedDocumentacion={selectedDocumentacion} handleDetailClose={handleDetailClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose} color="secondary">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Agregar */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <AgregarDocumentacion handleClose={() => setOpen(false)} onUpdate={handleDocumentoUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Editar */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogContent>
          <EditarDocumentacion documentacion={menuDoc} handleClose={() => setOpenEdit(false)} onUpdate={handleDocumentoUpdated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)} color="secondary">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Documentos;
